import React, { useState, useEffect } from 'react';
import { PlusIcon, XIcon, UsersIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LineupConnectionModal from './LineupConnectionModal';

/**
 * Component for managing lineup connections on sheet pages
 * Shows user's connections and provides ability to add new connections
 */
const SheetLineupManager = ({ sheetId }) => {
  const { user, isAuthenticated } = useAuth();
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [userConnections, setUserConnections] = useState([]);
  const [otherConnections, setOtherConnections] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch connections when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated && sheetId) {
      fetchConnections();
    }
  }, [isAuthenticated, sheetId]);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/sheets/${sheetId}/lineups`, {
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        setUserConnections(result.data.userConnections || []);
        setOtherConnections(result.data.otherConnections || []);
      } else {
        console.error('Failed to fetch connections, status:', response.status);
        setUserConnections([]);
        setOtherConnections([]);
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
      setUserConnections([]);
      setOtherConnections([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (connectionData) => {
    try {
      // Debug logging
      console.log('handleConnect called with:', connectionData);
      console.log('User object:', user);
      console.log('isAuthenticated:', isAuthenticated);

      // Check if user is available
      if (!isAuthenticated) {
        throw new Error(`User not authenticated. isAuthenticated: ${isAuthenticated}`);
      }

      // Make API call to connect lineup
      const response = await fetch(`/api/sheets/${sheetId}/lineups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          bandId: connectionData.bandId,
          lineupId: connectionData.lineupId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to connect lineup');
      }

      const result = await response.json();
      console.log('Connection result:', result);

      // Refresh connections to get updated data
      await fetchConnections();
      
      // Show success message
      alert(`Successfully connected ${connectionData.type === 'lineup' ? 'lineup' : 'band'}!`);
    } catch (error) {
      console.error('Error connecting lineup:', error);
      throw error;
    }
  };

  const handleRemoveConnection = async (connectionId) => {
    if (!window.confirm('Remove this lineup connection?')) return;

    try {
      const response = await fetch(`/api/sheets/${sheetId}/lineups`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ connectionId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove connection');
      }

      // Refresh connections to get updated data
      await fetchConnections();
      alert('Connection removed successfully!');
    } catch (error) {
      console.error('Error removing connection:', error);
      alert('Failed to remove connection. Please try again.');
    }
  };


  // Debug logging
  console.log('SheetLineupManager render - user:', user, 'isAuthenticated:', isAuthenticated, 'userConnections:', userConnections.length, 'otherConnections:', otherConnections.length);
  
  // Additional debug to track when user gets populated
  useEffect(() => {
    if (user) {
      console.log('SheetLineupManager - USER LOADED:', user);
    }
  }, [user]);

  // Show login prompt if user is not authenticated
  if (!isAuthenticated) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
        <p className="text-sm text-gray-600">Log in to manage band lineups for this sheet</p>
      </div>
    );
  }

  // Don't show if no connections and no ability to add (loading state)
  if (loading) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-500">Loading lineup connections...</p>
      </div>
    );
  }

  // Determine if user is fully loaded and ready
  // For now, trust isAuthenticated even if user object is loading
  const userReady = isAuthenticated;

  return (
    <div className="mt-4">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900">Band Lineups</h3>
        <button
          onClick={() => setShowConnectionModal(true)}
          className={`flex items-center px-3 py-1 rounded-md transition-colors text-sm ${
            userReady 
              ? 'bg-blue-500 text-white hover:bg-blue-600' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          disabled={!userReady}
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          {userReady ? 'Add Lineup' : 'Loading...'}
        </button>
      </div>

      {/* User's Connections */}
      {userConnections.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Your lineups:</h4>
          <div className="space-y-2">
            {userConnections.map(connection => (
              <div key={connection.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="flex items-center flex-1">
                  <UsersIcon className="w-4 h-4 mr-2 text-blue-600" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {connection.bandName}
                      {connection.lineupName && (
                        <span className="text-blue-600"> - {connection.lineupName}</span>
                      )}
                    </div>
                    {connection.type === 'band' && (
                      <div className="text-xs text-gray-500">General band connection</div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Remove Connection */}
                  <button
                    onClick={() => handleRemoveConnection(connection.id)}
                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                    title="Remove connection"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other Users' Connections */}
      {otherConnections.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Also used by:</h4>
          <div className="space-y-2">
            {otherConnections.map(connection => (
              <div key={connection.id} className="flex items-center p-3 bg-gray-50 border border-gray-100 rounded-lg">
                <UsersIcon className="w-4 h-4 mr-2 text-gray-500" />
                <div className="flex-1">
                  <div className="text-sm text-gray-700">
                    {connection.bandName}
                    {connection.lineupName && (
                      <span className="text-gray-600"> - {connection.lineupName}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    Connected by {connection.connectedBy}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {userConnections.length === 0 && otherConnections.length === 0 && (
        <div className="text-center py-6 text-gray-500">
          <UsersIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No lineup connections yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Connect a band lineup to show it when printing this sheet
          </p>
        </div>
      )}

      {/* Connection Modal */}
      <LineupConnectionModal
        isOpen={showConnectionModal}
        onClose={() => setShowConnectionModal(false)}
        onConnect={handleConnect}
        sheetId={sheetId}
      />
    </div>
  );
};

export default SheetLineupManager;